import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { QualityPillars } from "../components/QualityPillars";
import { CTABand } from "../components/CTABand";
import { MATERIALS } from "../data/content";
import { Box, ShieldCheck, Palette, Droplet, Link2, Wrench, Hammer, DoorClosed, LayoutGrid } from "lucide-react";

const ICONS = [Box, ShieldCheck, Palette, Droplet, Link2, Wrench, Hammer, DoorClosed, LayoutGrid];

export default function Materials() {
  return (
    <>
      <SEO
        title="Materials Matter — Plywood, WPC, Laminates & Hardware | Paramount Kitchen"
        description="The materials behind Paramount Kitchen: plywood in 19/16/9 mm, high-grade WPC protection, 0.8 mm laminates, adhesives, edge banding and premium hardware — selected according to application."
      />

      <section className="bg-royal pb-20 pt-36 text-ivory md:pb-28 md:pt-44">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow text-gold">Materials &amp; Hardware</p>
            <h1 className="text-balance mt-5 max-w-3xl font-display text-4xl font-medium leading-tight md:text-6xl">
              Materials <span className="serif-accent text-gold">matter.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-ivory/65">
              Every material in a Paramount Kitchen is chosen for the job it must do — and
              specifications are selected according to component and application.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {MATERIALS.map((m, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={m.name} delay={(i % 3) * 0.07} className="h-full">
                  <div
                    data-testid={`material-card-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-xl"
                  >
                    <div className="flex items-center justify-between">
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-sand text-wood transition-colors duration-500 group-hover:bg-gold group-hover:text-royal">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="rounded-full border border-gold/40 bg-sand px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-wood">
                        {m.spec}
                      </span>
                    </div>
                    <h2 className="mt-6 font-display text-2xl font-medium text-ink">{m.name}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">{m.text}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {m.chips.map((c) => (
                        <span
                          key={c}
                          className="rounded-full bg-ink/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/60"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-parch py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Our Standard</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Made with attention to{" "}
              <span className="serif-accent text-golddim">what you don&rsquo;t see.</span>
            </h2>
          </Reveal>
          <div className="mt-14">
            <QualityPillars />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
